import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useRouteError,
  isRouteErrorResponse,
  LinksFunction,
  MetaFunction,
} from "react-router";

import "./tailwind.css";
import { NextUIProvider } from "@nextui-org/react";
import { ThemeProvider } from "next-themes";
import { Toaster } from "react-hot-toast";
import { UserProvider } from "./stores/user";
import posthog from "posthog-js";
import { SearchProvider } from "./stores/search";
import Footer from "./components/footer";
import { useEffect } from "react";
import LocationWarningModal from "./components/location-warning-modal";
import { WishlistProvider } from "./stores/wishlist";
import BottomNavbar from "./components/bottom-navbar";
import NavigationProgress from "./components/navigation-progress";

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&display=swap",
  },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  useEffect(() => {
    posthog.capture("$pageview");
  }, [location]);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, user-scalable=no, minimum-scale=1, maximum-scale=1, viewport-fit=cover, theme-color=light"
        />
        <Meta />
        <Links />
        <link rel="icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" href="/favicon.ico" />
      </head>

      <body className="font-sans">
        <NextUIProvider locale="en-GB">
          <ThemeProvider attribute="class" defaultTheme="light">
            <UserProvider>
              <SearchProvider>
                <WishlistProvider>
                  <div className="relative min-h-screen flex flex-col">
                    {children}
                    <LocationWarningModal />
                    <Footer />
                    <BottomNavbar />
                    <NavigationProgress />
                  </div>
                </WishlistProvider>
              </SearchProvider>
            </UserProvider>
            <Toaster />
          </ThemeProvider>
        </NextUIProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const meta: MetaFunction = ({ error }) => {
  return [{ title: error ? "Oops!" : "Tribe" }];
};

function App() {
  return <Outlet />;
}

export default withSentry(App);

export function ErrorBoundary() {
  const error = useRouteError();

  captureRemixErrorBoundaryError(error);

  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4">
      {isRouteErrorResponse(error) ? (
        <>
          <h2 className="text-center font-extrabold text-7xl text-default-500">
            {error.status}
          </h2>
          <p className="text-center text-2xl">{error.statusText}</p>
        </>
      ) : error instanceof Error ? (
        <>
          <h2 className="text-center font-extrabold text-7xl text-default-500">
            {error.message}
          </h2>
          <p className="text-center text-2xl">The stack trace is:</p>
          <pre className="text-center text-2xl">{error.stack}</pre>
        </>
      ) : (
        <h2 className="text-center font-extrabold text-7xl text-default-500">
          Unknown Error
        </h2>
      )}
    </div>
  );
}
